import { useSyncExternalStore } from "react";

/**
 * Is this device talking to the server? — the ONE answer the OFFLINE system
 * screen (and anything else that cares) reads.
 *
 * Two signals feed it, because neither is enough alone:
 *
 * - the browser's `offline` / `online` events — instant, but `navigator.onLine`
 *   only knows about the NIC: a router with no uplink reports "online";
 * - a failed network `fetch` (a `TypeError`, which is what fetch throws for a
 *   connection that did not happen — an `AbortError` is a different class and a
 *   4xx/5xx is a reachable server). A single failure is NOT trusted on its own:
 *   it triggers a probe, and only a failed probe flips the app offline. That is
 *   what keeps one flaky request or a navigating-away abort from throwing the
 *   whole back office behind an "offline" screen.
 *
 * While the failure is the probe's finding, no browser event will announce the
 * recovery, so the outage pane makes up to three delayed recovery attempts,
 * then waits for focus, reconnect or explicit retry. Healthy screens never probe.
 *
 * Nothing here touches queued work: the offline screen is an overlay over a
 * still-mounted page (see `OfflinePane` / `ManageShell`), so unsaved form state
 * and any pending mutation survive the outage by construction.
 */
export type NetworkSnapshot = {
  /** True when the browser says so OR a probe found the API unreachable. */
  offline: boolean;
  /** Epoch ms of the last API response of any kind, or `null` if none yet. */
  lastSyncedAt: number | null;
};

type Probe = () => Promise<boolean>;

export type NetworkStore = {
  getSnapshot: () => NetworkSnapshot;
  subscribe: (listener: () => void) => () => void;
  /** A network-class `fetch` failure happened — verify it before believing it. */
  reportFailure: () => void;
  /** The API answered (any HTTP status). */
  reportSuccess: () => void;
  /** Probe now (the "Try again" button). Resolves true when back online. */
  retry: () => Promise<boolean>;
  setProbe: (probe: Probe) => void;
};

type WindowLike = Pick<Window, "addEventListener" | "removeEventListener"> & {
  navigator?: { onLine?: boolean };
};

export function createNetworkStore(
  win: WindowLike | undefined,
  now: () => number = Date.now,
): NetworkStore {
  let probe: Probe | null = null;
  let browserOffline = win?.navigator?.onLine === false;
  let probedOffline = false;
  let lastSyncedAt: number | null = null;
  let snapshot: NetworkSnapshot = { offline: browserOffline, lastSyncedAt };
  let probing: Promise<boolean> | null = null;
  const listeners = new Set<() => void>();

  const publish = () => {
    const offline = browserOffline || probedOffline;
    if (offline === snapshot.offline && lastSyncedAt === snapshot.lastSyncedAt) return;
    snapshot = { offline, lastSyncedAt };
    listeners.forEach((listener) => listener());
  };

  const runProbe = (): Promise<boolean> => {
    if (!probe) return Promise.resolve(!(browserOffline || probedOffline));
    if (probing) return probing;
    probing = probe()
      .catch(() => false)
      .then((reachable) => {
        probing = null;
        if (reachable) {
          probedOffline = false;
          browserOffline = false;
          lastSyncedAt = now();
        } else {
          probedOffline = true;
        }
        publish();
        return reachable;
      });
    return probing;
  };

  win?.addEventListener("offline", () => {
    browserOffline = true;
    publish();
  });
  win?.addEventListener("online", () => {
    // The NIC is back; whether the server is reachable is the probe's call.
    browserOffline = false;
    if (probedOffline) void runProbe();
    else publish();
  });

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    reportFailure() {
      if (!snapshot.offline) void runProbe();
    },
    reportSuccess() {
      lastSyncedAt = now();
      if (probedOffline) {
        probedOffline = false;
      }
      publish();
    },
    retry() {
      if (browserOffline && win?.navigator?.onLine === false) {
        return Promise.resolve(false);
      }
      browserOffline = false;
      return runProbe();
    },
    setProbe(next) {
      probe = next;
    },
  };
}

export const networkStore: NetworkStore = createNetworkStore(
  typeof window === "undefined" ? undefined : window,
);

export function useNetworkStatus(store: NetworkStore = networkStore): NetworkSnapshot {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
